import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Input,
  VStack,
  HStack,
  Text,
  Icon,
  Box,
  Kbd,
  Divider,
  Spinner,
} from '@chakra-ui/react';

import { getEmployeeProfilePath, getSelectedCompanyDashboardPath } from 'app/utils/routes';
import useSearchEmployees from 'app/hooks/useSearchEmployees';
import useSearchCompanies from 'app/hooks/useSearchCompanies';
import {
  BriefcaseOutline,
  ProfileCircleOutline,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import useCommands from './useCommands';
import { CommandPaletteProps, PaletteMode, IdEntityType } from './types';
import {
  ID_SELECT_ITEMS,
  PALETTE_COLORS,
  PALETTE_SPACING,
  PALETTE_SIZES,
  SEARCH_CONFIG,
} from './constants';
import CommandItem from './components/command-item/CommandItem';
import EmployeeItem from './components/employee-item/EmployeeItem';
import CompanyItem from './components/company-item/CompanyItem';
import EmptyState from './components/empty-state/EmptyState';

/**
 * @description Command Palette is a keyboard shortcut for quick actions.
 * Access in any page with `Cmd+K / Ctrl+K`
 */
function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [mode, setMode] = useState<PaletteMode>('default');
  const [idEntityType, setIdEntityType] = useState<IdEntityType>('company');
  const [search, setSearch] = useState('');
  const [idInput, setIdInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Prevents scroll-triggered onMouseEnter from overriding keyboard selection
  const isKeyboardNavigating = useRef(false);

  const enterIdSelect = useCallback(() => {
    setMode('id-select');
    setSearch('');
    setSelectedIndex(0);
    setTimeout(() => inputRef.current?.focus(), SEARCH_CONFIG.focusDelayMs);
  }, []);

  const commands = useCommands(enterIdSelect);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_CONFIG.debounceMs);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch employees and companies in parallel when search is 3+ characters (default mode only)
  const { data: employeeResults = [], isLoading: isLoadingEmployees } = useSearchEmployees(
    debouncedSearch,
    isOpen && mode === 'default'
  );
  const { data: companyResults = [], isLoading: isLoadingCompanies } = useSearchCompanies(
    debouncedSearch,
    isOpen && mode === 'default'
  );

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search.trim()) return commands;

    const searchLower = search.toLowerCase();
    return commands.filter((cmd) => {
      const labelMatch = cmd.label.toLowerCase().includes(searchLower);
      const descriptionMatch = cmd.description?.toLowerCase().includes(searchLower);
      const keywordMatch = cmd.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower)
      );

      return labelMatch || descriptionMatch || keywordMatch;
    });
  }, [commands, search]);

  // Filter id-select sub-items
  const filteredIdSelectItems = useMemo(() => {
    if (!search.trim()) return ID_SELECT_ITEMS;
    const searchLower = search.toLowerCase();
    return ID_SELECT_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(searchLower) ||
        item.type.toLowerCase().includes(searchLower)
    );
  }, [search]);

  // Reset selected index when mode or search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search, debouncedSearch, mode]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('default');
      setSearch('');
      setIdInput('');
      setSelectedIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, SEARCH_CONFIG.focusDelayMs);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = itemRefs.current.get(selectedIndex);
    if (selectedElement && typeof selectedElement.scrollIntoView === 'function') {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    // Re-enable hover selection after scroll settles, preventing scroll-triggered
    // onMouseEnter events from overriding the keyboard-selected index
    const frame = requestAnimationFrame(() => {
      isKeyboardNavigating.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedIndex]);

  // Used by onMouseEnter — ignored while a keyboard navigation is in flight
  const handleMouseSelect = (index: number) => {
    if (!isKeyboardNavigating.current) {
      setSelectedIndex(index);
    }
  };

  const handleSelect = (index: number) => {
    if (mode === 'default') {
      if (index < filteredCommands.length) {
        const command = filteredCommands[index];
        if (command) {
          command.action();
          if (!command.keepOpen) onClose();
        }
      } else if (index < filteredCommands.length + companyResults.length) {
        const company = companyResults[index - filteredCommands.length];
        if (company) {
          navigate(getSelectedCompanyDashboardPath(company.id));
          onClose();
        }
      } else {
        const employee = employeeResults[index - filteredCommands.length - companyResults.length];
        if (employee) {
          navigate(getEmployeeProfilePath(employee.id, employee.companyId));
          onClose();
        }
      }
    } else if (mode === 'id-select') {
      const item = filteredIdSelectItems[index];
      if (item) {
        setIdEntityType(item.type);
        setMode('id-input');
        setSearch('');
        setIdInput('');
        setTimeout(() => inputRef.current?.focus(), SEARCH_CONFIG.focusDelayMs);
      }
    }
  };

  const handleIdSubmit = () => {
    const trimmed = idInput.trim();
    if (!trimmed) return;
    if (idEntityType === 'company') {
      navigate(getSelectedCompanyDashboardPath(trimmed));
    } else {
      navigate(getEmployeeProfilePath(trimmed));
    }
    onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mode === 'id-input') {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleIdSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setMode('id-select');
        setIdInput('');
        setTimeout(() => inputRef.current?.focus(), SEARCH_CONFIG.focusDelayMs);
      }
      return;
    }

    const currentTotalItems =
      mode === 'id-select'
        ? filteredIdSelectItems.length
        : filteredCommands.length + employeeResults.length + companyResults.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentTotalItems > 0) {
        isKeyboardNavigating.current = true;
        setSelectedIndex((prev) => (prev + 1) % currentTotalItems);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentTotalItems > 0) {
        isKeyboardNavigating.current = true;
        setSelectedIndex((prev) => (prev - 1 + currentTotalItems) % currentTotalItems);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (currentTotalItems === 0) return;
      handleSelect(selectedIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (mode === 'id-select') {
        setMode('default');
        setSearch('');
        setTimeout(() => inputRef.current?.focus(), SEARCH_CONFIG.focusDelayMs);
      } else {
        onClose();
      }
    }
  };

  const handleItemClick = (index: number) => {
    handleSelect(index);
  };

  /* ---- derived values for rendering ---- */
  let inputPlaceholder = 'Search commands, employees or companies...';
  if (mode === 'id-input') {
    inputPlaceholder = `Enter ${
      idEntityType === 'company' ? 'company' : 'individual'
    } ID and press Enter...`;
  } else if (mode === 'id-select') {
    inputPlaceholder = 'Select entity type...';
  }

  let inputIcon = SearchNormalOutline;
  if (mode === 'id-input') {
    inputIcon = idEntityType === 'company' ? BriefcaseOutline : ProfileCircleOutline;
  }

  let modeLabel: string | null = null;
  if (mode === 'id-select') {
    modeLabel = 'Search by ID';
  } else if (mode === 'id-input') {
    modeLabel = `${idEntityType === 'company' ? 'Company' : 'Individual'} ID`;
  }

  const isBodyEmpty =
    mode === 'id-input' ||
    (mode === 'default' &&
      filteredCommands.length === 0 &&
      employeeResults.length === 0 &&
      companyResults.length === 0 &&
      !isLoadingEmployees &&
      !isLoadingCompanies) ||
    (mode === 'id-select' && filteredIdSelectItems.length === 0);

  let bodyContent;
  if (isBodyEmpty && mode !== 'id-input') {
    bodyContent = <EmptyState searchLength={debouncedSearch.length} />;
  } else if (mode === 'id-input') {
    bodyContent = (
      <HStack p={PALETTE_SPACING.sectionPadding} color={PALETTE_COLORS.textMuted} justify="center">
        <Text fontSize={PALETTE_SIZES.fontSize}>
          Paste or type the ID above and press{' '}
          <Kbd fontSize={PALETTE_SIZES.fontSizeSmall} px={PALETTE_SIZES.kbdPadding} py="2px">
            enter
          </Kbd>{' '}
          to navigate
        </Text>
      </HStack>
    );
  } else if (mode === 'id-select') {
    bodyContent = (
      <VStack spacing={0} align="stretch">
        {filteredIdSelectItems.map((item, index) => (
          <HStack
            key={item.type}
            role="button"
            tabIndex={0}
            ref={(el) => {
              if (el) itemRefs.current.set(index, el);
              else itemRefs.current.delete(index);
            }}
            p={PALETTE_SPACING.itemPadding}
            spacing={PALETTE_SPACING.itemSpacing}
            cursor="pointer"
            bg={index === selectedIndex ? PALETTE_COLORS.selectedBg : PALETTE_COLORS.transparent}
            _hover={{ bg: PALETTE_COLORS.selectedBg }}
            onClick={() => handleItemClick(index)}
            onMouseEnter={() => handleMouseSelect(index)}
            borderLeft={
              index === selectedIndex
                ? `${PALETTE_SIZES.borderWidth} solid`
                : `${PALETTE_SIZES.borderWidth} solid transparent`
            }
            borderLeftColor={
              index === selectedIndex ? PALETTE_COLORS.selected : PALETTE_COLORS.transparent
            }
          >
            <Icon
              as={item.type === 'company' ? BriefcaseOutline : ProfileCircleOutline}
              boxSize={PALETTE_SIZES.iconSize}
              color={
                index === selectedIndex ? PALETTE_COLORS.selected : PALETTE_COLORS.textSecondary
              }
            />
            <VStack align="start" spacing="2px" flex={1}>
              <Text
                fontSize={PALETTE_SIZES.fontSize}
                fontWeight={index === selectedIndex ? '600' : '500'}
                color={index === selectedIndex ? PALETTE_COLORS.selected : PALETTE_COLORS.text}
              >
                {item.label}
              </Text>
              <Text fontSize={PALETTE_SIZES.fontSizeSmall} color={PALETTE_COLORS.textSecondary}>
                {item.description}
              </Text>
            </VStack>
          </HStack>
        ))}
      </VStack>
    );
  } else {
    bodyContent = (
      <VStack spacing={0} align="stretch">
        {/* Commands Section */}
        {filteredCommands.map((command, index) => (
          <CommandItem
            key={command.id}
            command={command}
            index={index}
            isSelected={index === selectedIndex}
            onSelect={handleMouseSelect}
            onClick={handleItemClick}
            itemRef={(el) => {
              if (el) {
                itemRefs.current.set(index, el);
              } else {
                itemRefs.current.delete(index);
              }
            }}
          />
        ))}

        {/* Companies Section */}
        {debouncedSearch.length >= SEARCH_CONFIG.minCharsForSearch &&
          (companyResults.length > 0 || isLoadingCompanies) && (
            <>
              {filteredCommands.length > 0 && (
                <Divider borderColor={PALETTE_COLORS.border} my="4px" />
              )}
              <Box
                px={PALETTE_SPACING.sectionPadding}
                py={PALETTE_SPACING.sectionHeader}
                bg={PALETTE_COLORS.selectedBg}
              >
                <Text
                  fontSize={PALETTE_SIZES.fontSizeSmall}
                  fontWeight="600"
                  color={PALETTE_COLORS.selected}
                >
                  COMPANIES
                </Text>
              </Box>
              {isLoadingCompanies ? (
                <HStack p={PALETTE_SPACING.sectionPadding} justify="center">
                  <Spinner size="sm" color={PALETTE_COLORS.selected} />
                  <Text fontSize={PALETTE_SIZES.fontSize} color={PALETTE_COLORS.textMuted}>
                    Searching companies...
                  </Text>
                </HStack>
              ) : (
                companyResults.map((company, compIndex) => {
                  const index = filteredCommands.length + compIndex;
                  return (
                    <CompanyItem
                      key={company.id}
                      company={company}
                      index={index}
                      isSelected={index === selectedIndex}
                      onSelect={handleMouseSelect}
                      onClick={handleItemClick}
                      itemRef={(el) => {
                        if (el) {
                          itemRefs.current.set(index, el);
                        } else {
                          itemRefs.current.delete(index);
                        }
                      }}
                    />
                  );
                })
              )}
            </>
          )}

        {/* Employees Section */}
        {debouncedSearch.length >= SEARCH_CONFIG.minCharsForSearch &&
          (employeeResults.length > 0 || isLoadingEmployees) && (
            <>
              {(filteredCommands.length > 0 || companyResults.length > 0) && (
                <Divider borderColor={PALETTE_COLORS.border} my="4px" />
              )}
              <Box
                px={PALETTE_SPACING.sectionPadding}
                py={PALETTE_SPACING.sectionHeader}
                bg={PALETTE_COLORS.selectedBg}
              >
                <Text
                  fontSize={PALETTE_SIZES.fontSizeSmall}
                  fontWeight="600"
                  color={PALETTE_COLORS.selected}
                >
                  EMPLOYEES
                </Text>
              </Box>
              {isLoadingEmployees ? (
                <HStack p={PALETTE_SPACING.sectionPadding} justify="center">
                  <Spinner size="sm" color={PALETTE_COLORS.selected} />
                  <Text fontSize={PALETTE_SIZES.fontSize} color={PALETTE_COLORS.textMuted}>
                    Searching employees...
                  </Text>
                </HStack>
              ) : (
                employeeResults.map((employee, empIndex) => {
                  const index = filteredCommands.length + companyResults.length + empIndex;
                  return (
                    <EmployeeItem
                      key={employee.id}
                      employee={employee}
                      index={index}
                      isSelected={index === selectedIndex}
                      onSelect={handleMouseSelect}
                      onClick={handleItemClick}
                      itemRef={(el) => {
                        if (el) {
                          itemRefs.current.set(index, el);
                        } else {
                          itemRefs.current.delete(index);
                        }
                      }}
                    />
                  );
                })
              )}
            </>
          )}
      </VStack>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        mt={PALETTE_SIZES.modalTop}
        maxH={PALETTE_SIZES.modalMaxHeight}
        overflow="hidden"
      >
        <ModalBody p={0}>
          <VStack spacing={0} align="stretch">
            {/* Search Input */}
            <HStack
              p={PALETTE_SPACING.sectionPadding}
              borderBottom="1px solid"
              borderColor={PALETTE_COLORS.border}
            >
              <Icon
                as={inputIcon}
                boxSize={PALETTE_SIZES.iconSize}
                color={PALETTE_COLORS.textSecondary}
              />
              {modeLabel && (
                <Box
                  bg="purple.100"
                  color="purple.700"
                  fontSize={PALETTE_SIZES.fontSizeSmall}
                  px="8px"
                  py="2px"
                  borderRadius="4px"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {modeLabel}
                </Box>
              )}
              <Input
                ref={inputRef}
                value={mode === 'id-input' ? idInput : search}
                onChange={(e) =>
                  mode === 'id-input' ? setIdInput(e.target.value) : setSearch(e.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                variant="unstyled"
                fontSize={PALETTE_SIZES.fontSize}
                _placeholder={{ color: PALETTE_COLORS.textMuted }}
              />
              <HStack spacing="4px">
                <Kbd fontSize={PALETTE_SIZES.fontSizeSmall} px={PALETTE_SIZES.kbdPadding} py="2px">
                  esc
                </Kbd>
              </HStack>
            </HStack>

            {/* Command List */}
            <Box
              ref={listRef}
              maxH="calc(60vh - 64px)"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {bodyContent}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CommandPalette;
